import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, KeyRound, ArrowLeft, CheckCircle2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const TEAL = '#42CDD7';

export default function ClaimAdminPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [bootstrapEnabled, setBootstrapEnabled] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/auth/bootstrap-status`).then(r => setBootstrapEnabled(r.data.enabled)).catch(() => setBootstrapEnabled(false));
  }, []);

  if (!user) { navigate('/login'); return null; }

  const submit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await axios.post(`${API}/api/auth/claim-admin`, { token }, {
        withCredentials: true,
        headers: { 'X-Bootstrap-Token': token },
      });
      setDone(true);
      await checkAuth();
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to claim admin');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="claim-admin-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8" style={{ color: TEAL }} />
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-foreground">Claim admin</h1>
          <p className="text-sm text-muted-foreground mt-2">One-off bootstrap for a fresh deployment. Paste the <code className="text-xs bg-muted px-1 py-0.5 rounded">BOOTSTRAP_ADMIN_TOKEN</code> you set in your deployment env variables to promote <strong>{user.email}</strong> to admin.</p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6">
          {user.role === 'admin' ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: '#16A34A' }} />
              <p className="text-sm font-semibold text-foreground">You&apos;re already admin.</p>
              <p className="text-xs text-muted-foreground mt-1">Nothing to do here.</p>
            </div>
          ) : done ? (
            <div className="text-center py-4" data-testid="claim-admin-success">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: '#16A34A' }} />
              <p className="text-sm font-semibold text-foreground">Promoted to admin.</p>
              <p className="text-xs text-muted-foreground mt-1">For security, remove <code>BOOTSTRAP_ADMIN_TOKEN</code> from your env now.</p>
            </div>
          ) : bootstrapEnabled === false ? (
            <div className="text-center py-4 flex flex-col items-center gap-2">
              <AlertTriangle className="w-8 h-8" style={{ color: '#D97706' }} />
              <p className="text-sm font-semibold text-foreground">Bootstrap mode is disabled</p>
              <p className="text-xs text-muted-foreground text-left mt-2 bg-muted p-3 rounded-lg">
                To enable, add this to your deployment env variables:
                <br /><code className="block mt-1">BOOTSTRAP_ADMIN_TOKEN=&lt;a strong secret&gt;</code>
                Redeploy, then come back to this page.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-500" data-testid="claim-admin-error">{error}</div>}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Bootstrap token</label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input required type="password" value={token} onChange={e => setToken(e.target.value)} autoFocus data-testid="claim-admin-token"
                    placeholder="Paste your BOOTSTRAP_ADMIN_TOKEN"
                    className="w-full pl-10 pr-4 py-3 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7]" />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">The value you placed in the deployment env var <code>BOOTSTRAP_ADMIN_TOKEN</code>. Once promoted, remove that env var.</p>
              </div>
              <button type="submit" disabled={loading || !token} data-testid="claim-admin-submit"
                className="w-full py-3 text-sm font-semibold text-white rounded-xl disabled:opacity-40"
                style={{ backgroundColor: TEAL }}>
                {loading ? 'Claiming…' : 'Promote me to admin'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
